export interface WaitForElementArgs {
  selector?: string;
  text?: string;
  visible?: boolean;
  timeout?: number;
}

export interface WaitForElementResult {
  success: boolean;
  message: string;
  element?: {
    tagName: string;
    textContent?: string;
    visible: boolean;
  };
}

/**
 * Wait for an element to appear on the page.
 * This tool can wait for elements by CSS selector or text content, and optionally check visibility.
 */
export async function waitForElement(args: WaitForElementArgs): Promise<WaitForElementResult> {
  const timeout = args.timeout || 5000;
  const startTime = Date.now();

  if (!args.selector && !args.text) {
    throw new Error("Either selector or text must be provided for waitForElement");
  }

  // Helper function to check if element is visible
  const isVisible = (element: HTMLElement): boolean => {
    const style = window.getComputedStyle(element);
    return (
      style.display !== "none" &&
      style.visibility !== "hidden" &&
      style.opacity !== "0" &&
      element.offsetWidth > 0 &&
      element.offsetHeight > 0
    );
  };

  // Helper function to find element
  const findElement = (): HTMLElement | null => {
    // Try selector first
    if (args.selector) {
      const element = document.querySelector(args.selector);
      if (element && element instanceof HTMLElement) {
        if (args.visible === false || !args.visible) {
          // Don't check visibility
          return element;
        }
        if (isVisible(element)) {
          return element;
        }
        return null; // Element exists but not visible
      }
    }

    // Try by text content
    if (args.text) {
      const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT);
      let node: Node | null = walker.nextNode();
      while (node) {
        if (node.textContent?.trim() === args.text.trim()) {
          const element = node.parentElement;
          if (element && element instanceof HTMLElement) {
            if (args.visible === false || !args.visible) {
              // Don't check visibility
              return element;
            }
            if (isVisible(element)) {
              return element;
            }
          }
        }
        node = walker.nextNode();
      }
    }

    return null;
  };

  // Wait for element to appear
  while (Date.now() - startTime < timeout) {
    const element = findElement();
    if (element) {
      return {
        success: true,
        message: `Element found${args.selector ? ` with selector "${args.selector}"` : ""}${args.text ? ` with text "${args.text}"` : ""}`,
        element: {
          tagName: element.tagName,
          textContent: element.textContent?.trim().substring(0, 100),
          visible: isVisible(element),
        },
      };
    }

    await new Promise((resolve) => setTimeout(resolve, 100));
  }

  throw new Error(
    `Element not found after ${timeout}ms${args.selector ? ` (looking for selector "${args.selector}")` : ""}${args.text ? ` (looking for text "${args.text}")` : ""}`,
  );
}
